import { Button, FormHelperText, Stack, Typography } from '@mui/material'
import React, { forwardRef, useEffect } from 'react'
import { FieldError, UseFormSetValue } from 'react-hook-form'
import { Select, TextInputField, TextInputFieldProps } from 'utoniq-ui'

import { BankAccountType, Maybe } from '@/clients/utoniq-core/schema'
import {
  tLabel,
  useForm,
  UseFormProps,
  useRouter,
  useTranslation,
} from '@/core'
import { Cell, CellFrame, Organism, OrganismFrame } from '@/core/architecture'

import type { Action } from '../template'

type BankCreateFormProps = {
  bank?: Maybe<BankValues>
  onSubmit: Action['onSubmit']
}

type BankValues = {
  bankName: string
  branchName: string
  branchCode: string
  accountType: BankAccountType | BankAccountType.Futsu
  accountNumber: string
  accountHolderName: string
}

const schema: UseFormProps['schema'] = o => ({
  bankName: o.string().label(tLabel('bankCreate.bankName')),
  branchName: o.string().label(tLabel('bankCreate.branchName')),
  branchCode: o
    .string()
    .length(3)
    .regex(/^[0-9]+$/)
    .messages({
      'string.pattern.base': tLabel('bankCreate.branchCodePattern'),
    })
    .label(tLabel('bankCreate.branchCode')),
  accountType: o.string().label(tLabel('bankCreate.accountType')),
  accountNumber: o
    .string()
    .length(7)
    .regex(/^[0-9]+$/)
    .messages({
      'string.pattern.base': tLabel('bankCreate.accountNumberPattern'),
    })
    .label(tLabel('bankCreate.accountNumber')),
  accountHolderName: o
    .string()
    .regex(/^[ァ-ヴ]+$/)
    .messages({
      'string.pattern.base': tLabel('bankCreate.accountHolderNamePattern'),
    })
    .label(tLabel('bankCreate.accountHolderName')),
})

export const BankCreateForm: Organism<BankCreateFormProps> = ({
  bank,
  onSubmit,
}) => {
  const { t } = useTranslation('bankCreate')
  const { router } = useRouter()

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    register,
    watch,
    setValue,
    reset,
  } = useForm<BankValues>({
    schema,
    defaultValues,
  })

  const accountTypeWatch = watch('accountType')

  useEffect(() => {
    if (!bank) return

    reset({
      bankName: bank.bankName,
      branchName: bank.branchName,
      branchCode: bank.branchCode,
      accountType: bank.accountType,
      accountNumber: bank.accountNumber,
      accountHolderName: bank.accountHolderName,
    })
  }, [bank, reset])

  const handleOnSubmit = handleSubmit(async input => {
    await onSubmit(input)
  })

  return (
    <OrganismFrame>
      <TextInputCell
        type="text"
        error={errors?.bankName}
        {...register('bankName')}
      />
      <TextInputCell
        type="text"
        error={errors?.branchName}
        {...register('branchName')}
      />
      <TextInputCell
        type="tel"
        error={errors?.branchCode}
        {...register('branchCode')}
      />
      <SelectCell
        value={accountTypeWatch}
        error={errors?.accountType}
        setValue={setValue}
      />
      <TextInputCell
        type="tel"
        error={errors?.accountNumber}
        {...register('accountNumber')}
      />
      <TextInputCell
        type="text"
        error={errors?.accountHolderName}
        {...register('accountHolderName')}
      />
      <Stack spacing={1} sx={{ px: 4, pb: 8 }} alignItems="center">
        <Button
          size="large"
          sx={{ width: 244 }}
          loading={isSubmitting}
          disabled={isSubmitting}
          onClick={handleOnSubmit}
        >
          {t('submit')}
        </Button>
        <Button
          variant="text"
          size="large"
          sx={{ width: 244 }}
          onClick={() => router.goBack()}
        >
          {t('back')}
        </Button>
      </Stack>
    </OrganismFrame>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('bankCreate')
    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

type SelectCellProps = {
  value: BankAccountType | ''
  setValue: UseFormSetValue<BankValues>
  error?: FieldError
}
const SelectCell: Cell<SelectCellProps> = ({ value, setValue, error }) => {
  const { t } = useTranslation('bankCreate')
  return (
    <CellFrame sx={{ mb: 5 }}>
      <Stack spacing={2} sx={{ pb: 5 }}>
        <Typography variant="subtitle1" color="text.secondary">
          {t('accountTypeTitle')}
        </Typography>
        <Stack>
          <Select
            listItems={[
              { value: BankAccountType.Futsu, label: t('futsu') },
              { value: BankAccountType.Toza, label: t('toza') },
            ]}
            placeholder={t('accountTypePlaceholder')}
            value={value}
            onChange={value =>
              setValue('accountType', value as BankAccountType)
            }
            formControlProps={{
              sx: {
                minWidth: 120,
                width: 120,
                bgcolor: 'grey.50',
                minHeight: 'auto',
              },
            }}
          />
          {error && <FormHelperText error>{error?.message}</FormHelperText>}
        </Stack>
      </Stack>
    </CellFrame>
  )
}

const defaultValues: BankValues = {
  bankName: '',
  branchName: '',
  branchCode: '',
  accountNumber: '',
  accountType: BankAccountType.Futsu,
  accountHolderName: '',
}
